/* TLF product gallery enhancements
   1. Page view: replace the bottom thumbnail strip with side arrows + dots.
   2. Full view (lightbox): main image on the left + a vertical scrolling
      thumbnail rail + close — with the image properly contained.
   Works on top of Dawn's media-gallery / product-modal and respects the
   color-variant filter (it ignores media hidden by `.media-hidden--color`). */
(function () {
  'use strict';

  /* ----------------------- Page view: arrows + dots ----------------------- */
  function arrowSvg(dir) {
    const pts = dir === 'prev' ? '15 18 9 12 15 6' : '9 18 15 12 9 6';
    return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><polyline points="${pts}"/></svg>`;
  }

  function makeArrow(dir) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'tlf-pv-arrow tlf-pv-arrow--' + dir;
    b.setAttribute('aria-label', dir === 'prev' ? 'Previous image' : 'Next image');
    b.innerHTML = arrowSvg(dir);
    return b;
  }

  function enhanceGallery(gallery) {
    if (gallery.dataset.tlfPv) return;
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    const list = viewer && viewer.querySelector('ul');
    if (!viewer || !list || typeof gallery.setActiveMedia !== 'function') return;
    gallery.dataset.tlfPv = '1';

    const prev = makeArrow('prev');
    const next = makeArrow('next');
    viewer.appendChild(prev);
    viewer.appendChild(next);

    const dots = document.createElement('div');
    dots.className = 'tlf-pv-dots';
    viewer.insertAdjacentElement('afterend', dots);

    const visible = () =>
      Array.prototype.slice
        .call(list.querySelectorAll('li[data-media-id]'))
        .filter((li) => !li.classList.contains('media-hidden--color'));

    const activeId = () => {
      const a = list.querySelector('li[data-media-id].is-active:not(.media-hidden--color)');
      if (a) return a.dataset.mediaId;
      const v = visible()[0];
      return v ? v.dataset.mediaId : null;
    };

    function go(delta) {
      const items = visible();
      if (items.length < 2) return;
      const cur = activeId();
      let idx = items.findIndex((li) => li.dataset.mediaId === cur);
      if (idx < 0) idx = 0;
      const target = items[(idx + delta + items.length) % items.length];
      gallery.setActiveMedia(target.dataset.mediaId, false);
    }

    prev.addEventListener('click', () => go(-1));
    next.addEventListener('click', () => go(1));

    let raf = null;
    function refresh() {
      const items = visible();
      const many = items.length > 1;
      prev.hidden = next.hidden = !many;
      dots.hidden = !many;
      const cur = activeId();
      dots.innerHTML = '';
      items.forEach((li) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'tlf-pv-dot' + (li.dataset.mediaId === cur ? ' is-active' : '');
        dot.setAttribute('aria-label', 'View image');
        dot.addEventListener('click', () => gallery.setActiveMedia(li.dataset.mediaId, false));
        dots.appendChild(dot);
      });
    }

    // Keep arrows/dots in sync with active media + color filter changes.
    const observer = new MutationObserver(() => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(refresh);
    });
    observer.observe(list, { attributes: true, attributeFilter: ['class'], subtree: true });

    refresh();
  }

  /* ----------------------- Full view: lightbox --------------------------- */
  function buildLightbox(modal, content) {
    if (!content) return;
    let sources = content.querySelectorAll('img[data-media-id]');
    if (!sources.length) sources = content.querySelectorAll(':scope > img, .product__media img');
    const imgs = Array.prototype.slice
      .call(sources)
      .filter((img) => !img.classList.contains('media-hidden--color'));

    const existing = content.querySelector('.tlf-lb');
    if (existing) existing.remove();

    if (!imgs.length) {
      content.classList.remove('tlf-lb-on');
      return;
    }
    content.classList.add('tlf-lb-on');

    const wrap = document.createElement('div');
    wrap.className = 'tlf-lb';

    const stage = document.createElement('div');
    stage.className = 'tlf-lb-stage';
    const stageImg = document.createElement('img');
    stageImg.className = 'tlf-lb-stage-img';
    stageImg.alt = '';
    stage.appendChild(stageImg);

    const rail = document.createElement('div');
    rail.className = 'tlf-lb-rail';

    // Dawn's media-modal closes on any mouse pointerup not inside deferred-media/
    // product-model. Stop pointerup from the image + rail so clicking a thumbnail
    // (or the image) switches/zooms instead of closing. Clicks on the surrounding
    // dark area still bubble up and close the modal.
    const stopClose = (e) => e.stopPropagation();
    stage.addEventListener('pointerup', stopClose);
    rail.addEventListener('pointerup', stopClose);

    function setActive(img, btn, instant) {
      rail.querySelectorAll('.tlf-lb-thumb').forEach((b) => b.classList.remove('is-active'));
      if (btn) {
        btn.classList.add('is-active');
        btn.scrollIntoView({ block: 'nearest', behavior: instant ? 'auto' : 'smooth' });
      }

      const newSrc = img.currentSrc || img.src;
      const apply = () => {
        stageImg.src = newSrc;
        if (img.srcset) {
          stageImg.srcset = img.srcset;
          stageImg.sizes = '90vw';
        }
        stageImg.alt = img.alt || '';
        requestAnimationFrame(() => stageImg.classList.remove('is-loading'));
      };

      if (instant) {
        apply();
        return;
      }
      // Crossfade: fade out, preload the new image, then swap + fade in.
      stageImg.classList.add('is-loading');
      const pre = new Image();
      pre.onload = apply;
      pre.onerror = apply;
      pre.src = newSrc;
    }

    const openedId = modal.openedBy && modal.openedBy.getAttribute('data-media-id');
    let activeIndex = imgs.findIndex((img) => img.dataset.mediaId === openedId);
    if (activeIndex < 0) activeIndex = 0;

    const buttons = imgs.map((img, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'tlf-lb-thumb';
      btn.setAttribute('aria-label', 'View image ' + (i + 1));
      const t = document.createElement('img');
      t.src = img.src;
      t.alt = '';
      t.loading = 'lazy';
      btn.appendChild(t);
      btn.addEventListener('click', () => setActive(img, btn));
      rail.appendChild(btn);
      return btn;
    });

    wrap.appendChild(stage);
    if (imgs.length > 1) wrap.appendChild(rail);
    content.appendChild(wrap);

    setActive(imgs[activeIndex], buttons[activeIndex], true);
  }

  // Patch the modal's own showActiveMedia so our builder always runs right after
  // it (openedBy + active media are set by then). More reliable than observing
  // the `open` attribute, since the modal is reparented to <body> on connect.
  function patchModal() {
    const ProductModal = window.customElements && customElements.get('product-modal');
    if (!ProductModal || ProductModal.prototype._tlfPatched) return;
    ProductModal.prototype._tlfPatched = true;

    const original = ProductModal.prototype.showActiveMedia;
    ProductModal.prototype.showActiveMedia = function () {
      if (typeof original === 'function') original.call(this);
      try {
        buildLightbox(this, this.querySelector('.product-media-modal__content'));
      } catch (e) {
        /* leave Dawn's default modal in place on error */
      }
    };
  }

  /* ----------------------------- Bootstrap ------------------------------- */
  function init() {
    document.querySelectorAll('media-gallery').forEach(enhanceGallery);
    patchModal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
